import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import SearchSelect from "./SearchSelect";
import { computeSpreadVsUsda, MIN_CASES_PER_WEEK } from "./fobPricesCalc";
import { holtWintersForecast, LBS_PER_TRUCK } from "./fobPricesForecast";
import { downloadXlsx } from "./xlsxExport";
import { loadFromSupabase } from "../supabaseDB";

/* FOB Prices — Fase 3B UI, fed for real (2026-09-12) by Sub-fase D.1's
   historical_prices table (usda-mars-ingest.js — see that file's header
   for why FR_FV110/Mexico-crossing districts is the real source), mapped
   onto this chart's row shape by mapHistoricalToUsdaRow() below. The
   synthetic 90-day mock series is now the only fallback, used only when
   historical_prices has no USDA rows yet. usda_market_prices (the
   original Fase 3B target) and its sync pipeline (netlify/functions/
   usda-prices-*.js) were deleted the same day — that pipeline never had
   real report slugs configured and its "Sync now" button just confused
   the working "Ejecutar ahora" button below with an unrelated warning.
   Pattern mirrors SeasonComparison.js
   for symmetry (season filter via seasonFilter.js, SearchSelect for every
   dropdown per CLAUDE.md's REGLA DE DROPDOWNS, xlsxExport for the Excel
   button) — this module is read-only market data, not a CRUD registry,
   so there's no create/edit form and REGLA DE VENTANAS's DraggableModal
   doesn't apply (nothing here opens into an edit view).

   Internal-price overlay (Clientes/Proveedores lines) only exists at
   WEEKLY granularity — fobPricesCalc.js's aggregateInternalPricesByWeek
   is inherently a weekly aggregate (cases-weighted avg per ISO week),
   there's no daily equivalent, so the chart's clients/suppliers toggle
   is disabled in Daily view rather than faking a daily number.

   Bands: only the USDA line gets a real min/max band (price_low/high is
   an actual USDA-reported range). Clientes/Proveedores are plotted as
   solid lines with no band — aggregateInternalPricesByWeek returns one
   point estimate (cases-weighted avg) per week, not a range, and
   fabricating a synthetic spread around it would misrepresent the data
   as more uncertain (or less) than it actually is. */

var T = {
  en: {
    title: "FOB Prices", subtitle: "USDA Market News vs GFI's own invoice/bill prices",
    never: "Never",
    source: "Source", sourceUsda: "USDA (USD)", sourceSniim: "SNIIM (MXN) — Michoacán",
    sniimNote: "SNIIM prices shown here are Michoacán only (Mercado de Abasto de Morelia) — the only market that reports zarzamora at $/box (4kg); other reporting markets use $/kg and were excluded to avoid mixing units.",
    priceUnitSniim: "MXN — price per box (4kg)", priceUnitUsda: "USD — price per package unit",
    product: "Product", category: "Category", size: "Size", seasons: "Seasons", dateRange: "Date range",
    rangeToday: "Today", range7: "7 days", range30: "30 days", range60: "60 days", range90: "90 days", rangeCustom: "Custom",
    from: "From", to: "To",
    priceLow: "Price Low", priceHigh: "Price High", movement: "Movement", trend: "Trend", remarks: "Remarks",
    spreadCard: "Spread vs USDA", trucksCard: "Trucks equivalent (7d)", vsClients: "vs Clients", vsSuppliers: "vs Suppliers",
    trucksAllProducts: "All products (Blackberry + Blueberry + Raspberry)",
    trucksCardNote: "Total US-bound import volume from Mexico (all importers, all border ports) per USDA MARS — not GFI's own shipments.",
    granDaily: "Daily", granWeekly: "Weekly",
    chartTitle: "Price trend", toggleClients: "USDA vs Clients", toggleSuppliers: "USDA vs Suppliers", toggleBoth: "USDA vs Both",
    seriesUsda: "USDA", seriesClients: "Clients (Invoices)", seriesSuppliers: "Suppliers (Bills)", seriesProjection: "Projection",
    projectionUnavailable: "Projection unavailable — not enough historical data for this filter yet",
    projectionTrendOnly: "Provisional trend-only projection (dashed line) — not enough history yet for a seasonal forecast. See the Forecast module for details.",
    overlayWeeklyOnly: "Clients/Suppliers overlay is only available in Weekly view.",
    tableTitle: "Historical prices", colDate: "Date", colWeek: "Week", colTrucks: "Trucks", colYoyLow: "YoY Low",
    search: "Search…", exportExcel: "Export to Excel", noRows: "No rows match the current filters.",
    emptyBanner: "Waiting for USDA API activation. Sync will start automatically once approved.",
    dataSourceMock: "Showing sample data (USDA sync not active yet)", dataSourceReal: "Live USDA data",
    lastIngestUsda: "Last USDA ingest", lastIngestSniim: "Last SNIIM ingest", totalHistoricalRecords: "Total historical records",
    runIngestNow: "Run now", runningIngest: "Running…", rows: "rows",
  },
  es: {
    title: "Precios FOB", subtitle: "USDA Market News vs precios propios de facturas/bills de GFI",
    never: "Nunca",
    source: "Fuente", sourceUsda: "USDA (USD)", sourceSniim: "SNIIM (MXN) — Michoacán",
    sniimNote: "Los precios de SNIIM que se muestran aquí son solo de Michoacán (Mercado de Abasto de Morelia) — es el único mercado que reporta zarzamora en $/caja (4kg); los demás mercados reportan en $/kilo y se excluyeron para no mezclar unidades.",
    priceUnitSniim: "MXN — precio por caja (4kg)", priceUnitUsda: "USD — precio por unidad de empaque",
    product: "Producto", category: "Categoría", size: "Tamaño", seasons: "Temporadas", dateRange: "Rango de fechas",
    rangeToday: "Hoy", range7: "7 días", range30: "30 días", range60: "60 días", range90: "90 días", rangeCustom: "Personalizado",
    from: "Desde", to: "Hasta",
    priceLow: "Precio Bajo", priceHigh: "Precio Alto", movement: "Movimiento", trend: "Tendencia", remarks: "Observaciones",
    spreadCard: "Spread vs USDA", trucksCard: "Trucks equivalentes (7d)", vsClients: "vs Clientes", vsSuppliers: "vs Proveedores",
    trucksAllProducts: "Todos los productos (Blackberry + Blueberry + Raspberry)",
    trucksCardNote: "Volumen total de importación a EE.UU. desde México (todos los importadores, todos los puertos fronterizos) según USDA MARS — no son los embarques propios de GFI.",
    granDaily: "Diario", granWeekly: "Semanal",
    chartTitle: "Tendencia de precios", toggleClients: "USDA vs Clientes", toggleSuppliers: "USDA vs Proveedores", toggleBoth: "USDA vs Ambos",
    seriesUsda: "USDA", seriesClients: "Clientes (Facturas)", seriesSuppliers: "Proveedores (Bills)", seriesProjection: "Proyección",
    projectionUnavailable: "Proyección no disponible — todavía no hay suficiente historial para este filtro",
    projectionTrendOnly: "Proyección provisional de solo tendencia (línea punteada) — aún no hay suficiente historial para un pronóstico estacional. Ver el módulo Proyecciones para más detalle.",
    overlayWeeklyOnly: "El comparativo de Clientes/Proveedores solo está disponible en vista Semanal.",
    tableTitle: "Precios históricos", colDate: "Fecha", colWeek: "Semana", colTrucks: "Trucks", colYoyLow: "YoY Bajo",
    search: "Buscar…", exportExcel: "Exportar a Excel", noRows: "Ningún registro coincide con los filtros actuales.",
    emptyBanner: "Esperando activación de la API de USDA. La sincronización iniciará automáticamente una vez aprobada.",
    dataSourceMock: "Mostrando datos de muestra (sync de USDA aún no activo)", dataSourceReal: "Datos en vivo de USDA",
    lastIngestUsda: "Última ingesta USDA", lastIngestSniim: "Última ingesta SNIIM", totalHistoricalRecords: "Total registros históricos",
    runIngestNow: "Ejecutar ahora", runningIngest: "Ejecutando…", rows: "filas",
  },
};

var PRODUCT_OPTIONS = ["Blackberry", "Blueberry", "Raspberry"];
var CATEGORY_OPTIONS = ["OG", "CV"];
var SIZE_OPTIONS = ["6oz", "12oz", "1pt", "otros"];
var MOVEMENT_COLORS = { Good: "#059669", Moderate: "#D97706", Light: "#DC2626" };
var TREND_ARROW = { Higher: "▲", Lower: "▼", Steady: "—" };
var TREND_COLOR = { Higher: "#059669", Lower: "#DC2626", Steady: "var(--color-text-muted)" };
/* usdaMid gets its own color (2026-09-12, Arturo) — the band and its
   mid-line used to share CHART_COLORS.usda, making them indistinguishable
   in both the chart and the legend, unlike Clients/Suppliers which
   already each had their own. Dark navy reads as "the trend line" over
   the light blue band's fill without competing with Clients (green) /
   Suppliers (orange). */
var CHART_COLORS = { usda: "#2563EB", usdaMid: "#0F172A", clients: "#059669", suppliers: "#D97706", projection: "#6B7280" };
/* Historical table columns whose values are right-aligned numbers
   (Trucks/Price Low/Price High/YoY Low) — see fix-dashboard.md's
   AJUSTE 1. Everything else (Date/Week/Movement/Trend/Remarks) stays
   left-aligned. */
var RIGHT_NUM_COLS = { trucksEquivalent: true, priceLow: true, priceHigh: true, yoyLow: true };

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }

/* Same ISO-8601 week algorithm as fobPricesCalc.js / SalesReports.js /
   BillRegistry.js etc. — duplicated per this codebase's per-file
   convention, kept byte-identical for consistent week numbering. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 864e5 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}
function toISO(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }
function round2(n) { return Math.round(n * 100) / 100; }
/* prefix defaults to "$" (USDA/USD) — pass "MX$" for SNIIM/MXN call
   sites so a peso figure never silently reads as if it were a dollar
   figure. */
function fmtMoney(n, prefix) { var v = parseFloat(n); if (isNaN(v)) return "—"; return (prefix || "$") + v.toFixed(2); }
function fmtDate(iso) { if (!iso) return "—"; return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function addDaysISO(iso, days) { var d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + days); return toISO(d); }

/* Deterministic pseudo-random in [-1, 1) — NOT Math.random(). The mock
   series needs to look the same across re-renders / re-mounts (so the
   chart doesn't visibly jump every time a filter changes and this file
   regenerates rows), which Math.random() can't give us. */
function pseudoNoise(i) {
  var x = Math.sin(i * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

var MOCK_DAYS = 90;
var MOCK_BASE_PRICE = 18;
var MOCK_MOVEMENTS = ["Good", "Moderate", "Light"];
var MOCK_REMARKS = {
  en: ["Market steady, demand moderate.", "Light offerings, quality mixed.", "Good movement, quality generally good.", "Prices firm on tighter supplies.", "Demand exceeds available supply."],
  es: ["Mercado estable, demanda moderada.", "Oferta ligera, calidad variable.", "Buen movimiento, calidad generalmente buena.", "Precios firmes por oferta más ajustada.", "La demanda supera la oferta disponible."],
};
/* Placeholder cases-per-truck conversion factor — refine once real USDA
   Market News volume units are confirmed (see usda-prices-sync.js's
   mapReportRowToPriceRow TODO; volume_unit isn't always "cases"). */
var MOCK_CASES_PER_TRUCK = 1200;

/* ~90 business days of synthetic USDA-shaped rows for Blackberry OG 6oz
   @ McAllen TX, with seasonality + linear trend + weekly wave + noise —
   just enough shape to exercise the chart/table/cards visually. Shaped
   like an app-record from usda_market_prices (camelCase, matches
   supabaseDB.js's mapper) so swapping in real Supabase rows later is a
   no-op for every consumer below. */
function generateMockUsdaRows(lang) {
  var rows = [];
  var today = new Date(); today.setHours(0, 0, 0, 0);
  var dayIdx = 0;
  for (var i = MOCK_DAYS - 1; i >= 0; i--) {
    var d = new Date(today); d.setDate(d.getDate() - i);
    var dow = d.getDay();
    if (dow === 0 || dow === 6) continue; // shipping-point reports publish business days only
    var seasonality = Math.sin((dayIdx / MOCK_DAYS) * Math.PI * 2) * 2.5;
    var trendUp = (dayIdx / MOCK_DAYS) * 3;
    var weeklyWave = Math.sin((dayIdx / 7) * Math.PI * 2) * 0.8;
    var noise = pseudoNoise(dayIdx);
    var base = MOCK_BASE_PRICE + seasonality + trendUp + weeklyWave + noise * 0.6;
    var prevBase = MOCK_BASE_PRICE + Math.sin(((dayIdx - 1) / MOCK_DAYS) * Math.PI * 2) * 2.5 + ((dayIdx - 1) / MOCK_DAYS) * 3 + Math.sin(((dayIdx - 1) / 7) * Math.PI * 2) * 0.8 + pseudoNoise(dayIdx - 1) * 0.6;
    var spread = 2 + Math.abs(weeklyWave);
    var priceLow = Math.max(4, round2(base - spread / 2));
    var priceHigh = round2(base + spread / 2);
    var movement = MOCK_MOVEMENTS[dayIdx % MOCK_MOVEMENTS.length];
    var trendLabel = base > prevBase + 0.15 ? "Higher" : base < prevBase - 0.15 ? "Lower" : "Steady";
    var remarksArr = MOCK_REMARKS[lang] || MOCK_REMARKS.en;
    var volumeOriginal = Math.round(750 + weeklyWave * 80 + noise * 60);
    var iso = toISO(d);
    rows.push({
      id: "mock-" + iso,
      reportDate: iso,
      weekIso: "W" + getISOWeek(iso),
      year: d.getFullYear(),
      product: "Blackberry",
      category: "OG",
      size: "6oz",
      marketLocation: "McAllen TX",
      priceLow: priceLow,
      priceHigh: priceHigh,
      movement: movement,
      trend: trendLabel,
      remarks: remarksArr[dayIdx % remarksArr.length],
      volumeOriginal: volumeOriginal,
      volumeUnit: "cases",
      trucksEquivalent: round2(volumeOriginal / MOCK_CASES_PER_TRUCK),
      sourceReportId: "MOCK-MCALLEN-BLACKBERRY-OG-6OZ",
    });
    dayIdx++;
  }
  return rows;
}

/* Maps one historical_prices app-row (source: "USDA", see
   usda-mars-ingest.js / supabaseDB.js's TABLE_CONFIG.historical_prices)
   onto the exact shape the chart/table below expects — the same shape
   generateMockUsdaRows() produces, matching usda_market_prices'
   camelCase mapper. historical_prices has no movement/trend/volume
   fields (FR_FV110 doesn't report truck volume, only price + tone
   comments), so those stay null rather than fabricated. */
function normalizeSizeToken(pkg) {
  if (!pkg) return "otros";
  var m = /(\d+(?:\.\d+)?)\s*oz\b/i.exec(pkg);
  if (m) return m[1] + "oz";
  if (/1\s*pt\b/i.test(pkg)) return "1pt";
  return "otros";
}
function mapHistoricalToUsdaRow(hp) {
  var iso = hp.reportDate;
  return {
    id: hp.id || [hp.reportSource, hp.product, hp.marketCity, iso].join("-"),
    reportDate: iso,
    weekIso: "W" + getISOWeek(iso),
    year: iso ? parseInt(iso.slice(0, 4), 10) : null,
    product: hp.product,
    category: hp.category === "Organic" ? "OG" : hp.category === "Conventional" ? "CV" : hp.category,
    size: normalizeSizeToken(hp.packageSize),
    marketLocation: hp.marketCity,
    priceLow: hp.priceLow,
    priceHigh: hp.priceHigh,
    movement: null,
    trend: null,
    remarks: (hp.extra && (hp.extra.rep_cmt || hp.extra.market_tone_comments)) || "",
    volumeOriginal: null,
    volumeUnit: null,
    trucksEquivalent: null,
    sourceReportId: hp.reportSource,
  };
}

/* Local (not fobPricesCalc.js) multi-select-capable version of
   aggregateInternalPricesByWeek — the exported helper's product/
   category/size params are single exact-match values (that's what's
   unit-tested in fobPricesCalc.test.js), but this module's filter bar
   is multi-select per the Fase 3B spec. Same status==="paid" /
   saleStatus==="goodDelivery" / MIN_CASES_PER_WEEK filtering rules,
   just with `products`/`categories`/`sizes` arrays (empty array = no
   restriction on that dimension, matching the filter bar's own "nothing
   checked = show all" default). Kept here rather than added to
   fobPricesCalc.js so that file's tested single-value contract doesn't
   have to grow a second calling convention. */
function aggregateInternalMulti(invoicesOrBills, products, categories, sizes) {
  var pSet = products && products.length ? new Set(products.map(function (x) { return String(x).toLowerCase(); })) : null;
  var cSet = categories && categories.length ? new Set(categories.map(function (x) { return String(x).toLowerCase(); })) : null;
  var sSet = sizes && sizes.length ? new Set(sizes.map(function (x) { return String(x).toLowerCase(); })) : null;
  var buckets = {};
  (invoicesOrBills || []).forEach(function (rec) {
    if (!rec || rec.status !== "paid") return;
    var d = rec.date || rec.billDate || "";
    if (!d) return;
    var year = new Date(d + "T00:00:00").getFullYear();
    var weekIso = "W" + getISOWeek(d);
    (rec.lines || []).forEach(function (l) {
      if (!l || l.saleStatus !== "goodDelivery") return;
      var product = String(l.productName || l.productBase || "").toLowerCase();
      if (pSet && !pSet.has(product)) return;
      if (cSet && !cSet.has(String(l.category || "").toLowerCase())) return;
      if (sSet && !sSet.has(String(l.size || "").toLowerCase())) return;
      var cases = parseInt(l.cases, 10) || 0;
      if (cases <= 0) return;
      var price = l.salePrice !== "" && l.salePrice != null && !isNaN(parseFloat(l.salePrice)) ? parseFloat(l.salePrice) : (parseFloat(l.reportedPrice) || 0);
      var key = year + "|" + weekIso;
      if (!buckets[key]) buckets[key] = { year: year, weekIso: weekIso, cases: 0, weightedSum: 0 };
      buckets[key].cases += cases;
      buckets[key].weightedSum += cases * price;
    });
  });
  return Object.keys(buckets).map(function (k) { return buckets[k]; })
    .filter(function (b) { return b.cases >= MIN_CASES_PER_WEEK; })
    .map(function (b) { return { year: b.year, weekIso: b.weekIso, cases: b.cases, avgPrice: b.weightedSum / b.cases }; })
    .sort(function (a, b) { return (a.year - b.year) || (a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0); });
}

function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(function () { onClose(); }, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "var(--color-accent)", color: "var(--color-accent-text)", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999999, boxShadow: "0 8px 24px var(--color-shadow)", animation: fading ? "fadeOut 0.3s ease forwards" : "fadeIn 0.2s ease", maxWidth: 420, textAlign: "center" }}>{message}</div>, document.body);
}

function Card({ children }) {
  return <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "14px 16px", minWidth: 180, flex: "1 1 180px" }}>{children}</div>;
}

export default function FobPricesRegistry({ lang }) {
  var t = T[lang] || T.en;

  var [usdaRows, setUsdaRows] = useState([]);
  var [dataSource, setDataSource] = useState("none"); // 'real' | 'mock' | 'none'
  /* 2026-09-12 — which historical_prices source feeds this whole screen.
     USDA is USD, SNIIM is MXN — showing both on one $ chart/spread-vs-
     GFI-price card at once would silently mix currencies, so the user
     picks one at a time (Arturo: "si selecciono fuente USDA muestra
     USA, si selecciono SNIIM muestra méxico") rather than the app
     guessing how to blend them. */
  var [priceSource, setPriceSource] = useState("USDA");
  var [toast, setToast] = useState(null);

  /* Sub-fase D.1 — USDA MARS border-price + SNIIM ingest status. */
  var [historicalStatus, setHistoricalStatus] = useState({ usda: null, sniim: null });
  var [historicalTotal, setHistoricalTotal] = useState(0);
  var [runningIngest, setRunningIngest] = useState(false);

  var [invoices, setInvoices] = useState([]);
  var [bills, setBills] = useState([]);

  var [selProducts, setSelProducts] = useState([]);
  var [selCategories, setSelCategories] = useState([]);
  var [selSizes, setSelSizes] = useState([]);
  var [rangeKey, setRangeKey] = useState("today");
  var [customFrom, setCustomFrom] = useState("");
  var [customTo, setCustomTo] = useState("");

  var [granularity, setGranularity] = useState(function () { return loadLS("gfi-fob-prices-granularity", "weekly"); });
  useEffect(function () { try { localStorage.setItem("gfi-fob-prices-granularity", JSON.stringify(granularity)); } catch (e) {} }, [granularity]);

  var [chartMode, setChartMode] = useState("both"); // 'clients' | 'suppliers' | 'both'
  var [sortCol, setSortCol] = useState("reportDate");
  var [sortDir, setSortDir] = useState("desc");
  var [search, setSearch] = useState("");

  useEffect(function () {
    setInvoices(loadLS("gfi-invoices", []));
    setBills(loadLS("gfi-bills", []));
  }, []);

  /* 2026-09-15 — real truck_crossings data (USDA MARS movement, Mexico-
     origin only) feeding the "Trucks equivalent" card below, replacing
     the old mock-only trucksEquivalent field that was always null on
     real USDA price rows (see fobPricesForecast.js's LBS_PER_TRUCK for
     the real lb-per-truck conversion Arturo gave). Loaded once, filtered
     by product client-side like the rest of this screen's data. */
  var [truckRows, setTruckRows] = useState([]);
  useEffect(function () {
    loadFromSupabase("truck_crossings").then(function (rows) { setTruckRows(rows || []); }).catch(function () { setTruckRows([]); });
  }, []);

  /* historical_prices (Sub-fase D.1, usda-mars-ingest.js) is the only
     real pipeline now — usda_market_prices / usda-prices-sync.js (Fase
     3B) was deleted 2026-09-12: it never had real report slugs
     configured (REPORTS_TO_TRACK was an empty TODO stub) and its own
     "Sync now" button/warning was confusing next to the working
     "Ejecutar ahora" button below. Mock data is now the only fallback,
     used only when historical_prices has no USDA rows yet. */
  var loadPrices = useCallback(function () {
    loadFromSupabase("historical_prices").then(function (hpRows) {
      var sourceHp = (hpRows || []).filter(function (r) { return r.source === priceSource; });
      if (sourceHp.length > 0) {
        setUsdaRows(sourceHp.map(mapHistoricalToUsdaRow));
        setDataSource("real");
      } else if (priceSource === "USDA") {
        /* Mock fallback only ever made sense for the original USDA-only
           build — SNIIM never had a mock series, an empty SNIIM result
           is just "no rows yet", not "sync never ran". */
        setUsdaRows(generateMockUsdaRows(lang));
        setDataSource("mock");
      } else {
        setUsdaRows([]);
        setDataSource("real");
      }
    }).catch(function () {
      if (priceSource === "USDA") { setUsdaRows(generateMockUsdaRows(lang)); setDataSource("mock"); }
      else { setUsdaRows([]); setDataSource("real"); }
    });
  }, [lang, priceSource]);

  useEffect(function () { loadPrices(); }, [loadPrices]);

  var loadHistoricalStatus = useCallback(function () {
    loadFromSupabase("historical_prices_sync_state").then(function (rows) {
      var bySource = {};
      (rows || []).forEach(function (r) { bySource[r.source] = r; });
      setHistoricalStatus({ usda: bySource.USDA || null, sniim: bySource.SNIIM || null });
    }).catch(function () {});
    loadFromSupabase("historical_prices").then(function (rows) {
      setHistoricalTotal((rows || []).length);
    }).catch(function () {});
  }, []);

  useEffect(function () { loadHistoricalStatus(); }, [loadHistoricalStatus]);

  /* CAMBIO 6 — "Ejecutar ahora" fires both ingest functions for manual
     testing. Promise.allSettled so a USDA failure doesn't block SNIIM
     (and vice versa) from reporting its own result in the toast. */
  var handleRunIngestNow = function () {
    setRunningIngest(true);
    Promise.allSettled([
      fetch("/.netlify/functions/usda-mars-ingest", { method: "POST" }).then(function (res) { return res.json().catch(function () { return {}; }); }),
      fetch("/.netlify/functions/sniim-ingest", { method: "POST" }).then(function (res) { return res.json().catch(function () { return {}; }); }),
    ]).then(function (settled) {
      var usdaResult = settled[0].status === "fulfilled" ? settled[0].value : { error: String(settled[0].reason) };
      var sniimResult = settled[1].status === "fulfilled" ? settled[1].value : { error: String(settled[1].reason) };
      var summary = "USDA: " + (usdaResult.rowsIngested != null ? usdaResult.rowsIngested + " " + t.rows : (usdaResult.warning || usdaResult.error || "?"))
        + " · SNIIM: " + (sniimResult.rowsIngested != null ? sniimResult.rowsIngested + " " + t.rows : (sniimResult.warning || sniimResult.error || "?"));
      setToast(summary);
      loadHistoricalStatus();
    }).finally(function () { setRunningIngest(false); });
  };

  var currencyPrefix = priceSource === "SNIIM" ? "MX$" : "$";

  /* Effective [from, to] ISO date bounds for the global date-range filter. */
  var dateBounds = useMemo(function () {
    var latest = usdaRows.reduce(function (acc, r) { return r.reportDate && r.reportDate > acc ? r.reportDate : acc; }, "");
    var to = latest || toISO(new Date());
    if (rangeKey === "custom") return { from: customFrom || addDaysISO(to, -30), to: customTo || to };
    var days = rangeKey === "today" ? 0 : rangeKey === "7" ? 7 : rangeKey === "60" ? 60 : rangeKey === "90" ? 90 : 30;
    return { from: addDaysISO(to, -days), to: to };
  }, [usdaRows, rangeKey, customFrom, customTo]);

  /* No season filter here (2026-09-12, Arturo) — unlike every other
     dated module, these rows are external market reference data (USDA/
     SNIIM), not GFI's own season-scoped records. Restricting to "open
     seasons" by default was hiding real historical data (e.g. closed-
     season months) the user explicitly wants full, unrestricted access
     to. This is this module's REGLA DE FILTRO DE SEASON exception —
     see CLAUDE.md's "Excepciones permitidas" list for the catalog-
     module precedent this follows. */
  var filteredUsdaRows = useMemo(function () {
    var pSet = selProducts.length ? new Set(selProducts) : null;
    var cSet = selCategories.length ? new Set(selCategories) : null;
    var sSet = selSizes.length ? new Set(selSizes) : null;
    return usdaRows.filter(function (r) {
      if (!r.reportDate || r.reportDate < dateBounds.from || r.reportDate > dateBounds.to) return false;
      if (pSet && !pSet.has(r.product)) return false;
      if (cSet && !cSet.has(r.category)) return false;
      if (sSet && !sSet.has(r.size)) return false;
      return true;
    });
  }, [usdaRows, selProducts, selCategories, selSizes, dateBounds]);

  /* 2026-09-15 (Arturo): "tarjeta historical prices trucks devuelve 0 en
     todos los renglones" — historical_prices rows always carry
     trucksEquivalent:null (mapHistoricalToUsdaRow never had real volume
     data to compute it from). Real per-day/per-product totals now come
     from truck_crossings (loaded above for the summary card) — joined
     here by product+date rather than injected into the raw price rows,
     so a week that has BOTH an Organic and a Conventional price row for
     the same product+day doesn't double-count that day's real truck
     total twice. */
  var truckByProductDate = useMemo(function () {
    var m = {};
    truckRows.forEach(function (r) {
      var k = r.product + "|" + r.shipmentDate;
      m[k] = (m[k] || 0) + (parseFloat(r.lbUnits) || 0);
    });
    return m;
  }, [truckRows]);
  var truckByProductWeek = useMemo(function () {
    var m = {};
    truckRows.forEach(function (r) {
      if (!r.shipmentDate) return;
      var year = new Date(r.shipmentDate + "T00:00:00").getFullYear();
      var wk = "W" + getISOWeek(r.shipmentDate);
      var k = r.product + "|" + year + "|" + wk;
      m[k] = (m[k] || 0) + (parseFloat(r.lbUnits) || 0);
    });
    return m;
  }, [truckRows]);

  /* One row per day (as-is) or one row per ISO week (avg low/high,
     summed trucks, last movement/trend of that week) depending on the
     granularity toggle. */
  var periodRows = useMemo(function () {
    if (granularity === "daily") {
      return filteredUsdaRows.slice().sort(function (a, b) { return a.reportDate < b.reportDate ? -1 : 1; })
        .map(function (r) {
          var lb = truckByProductDate[r.product + "|" + r.reportDate] || 0;
          return Object.assign({}, r, { trucksEquivalent: round2(lb / LBS_PER_TRUCK) });
        });
    }
    var byWeek = {};
    filteredUsdaRows.forEach(function (r) {
      var key = r.year + "|" + r.weekIso;
      if (!byWeek[key]) byWeek[key] = { year: r.year, weekIso: r.weekIso, reportDate: r.reportDate, rows: [] };
      if (r.reportDate > byWeek[key].reportDate) byWeek[key].reportDate = r.reportDate;
      byWeek[key].rows.push(r);
    });
    return Object.keys(byWeek).map(function (k) {
      var g = byWeek[k];
      var lows = g.rows.map(function (r) { return r.priceLow; }).filter(function (v) { return v != null; });
      var highs = g.rows.map(function (r) { return r.priceHigh; }).filter(function (v) { return v != null; });
      /* Sum trucks once per distinct product in this week's bucket
         (never per price row) — a week bucket can only mix products
         when more than one is selected in the filter, same pre-existing
         ambiguity the price average already had; trucks just mirrors
         "whichever products are actually in this bucket". */
      var productsInWeek = {};
      g.rows.forEach(function (r) { productsInWeek[r.product] = true; });
      var trucks = round2(Object.keys(productsInWeek).reduce(function (s, p) {
        return s + (truckByProductWeek[p + "|" + g.year + "|" + g.weekIso] || 0);
      }, 0) / LBS_PER_TRUCK);
      var last = g.rows.slice().sort(function (a, b) { return a.reportDate < b.reportDate ? -1 : 1; }).slice(-1)[0];
      return {
        year: g.year, weekIso: g.weekIso, reportDate: g.reportDate,
        priceLow: lows.length ? round2(lows.reduce(function (s, v) { return s + v; }, 0) / lows.length) : null,
        priceHigh: highs.length ? round2(highs.reduce(function (s, v) { return s + v; }, 0) / highs.length) : null,
        trucksEquivalent: round2(trucks),
        movement: last ? last.movement : "", trend: last ? last.trend : "", remarks: last ? last.remarks : "",
        product: last ? last.product : "", category: last ? last.category : "", size: last ? last.size : "",
      };
    }).sort(function (a, b) { return a.reportDate < b.reportDate ? -1 : 1; });
  }, [filteredUsdaRows, granularity, truckByProductDate, truckByProductWeek]);

  var internalWeekly = useMemo(function () {
    var clients = aggregateInternalMulti(invoices, selProducts, selCategories, selSizes);
    var suppliers = aggregateInternalMulti(bills, selProducts, selCategories, selSizes);
    return { clients: clients, suppliers: suppliers };
  }, [invoices, bills, selProducts, selCategories, selSizes]);

  /* 2026-09-12 — fobPricesForecast.js's holtWintersForecast is now
     implemented (see that file's header for the trend-only/seasonal
     split and why today's data runs in trend-only mode). periodRows is
     already the same { year, weekIso, priceLow, priceHigh } shape the
     function expects, no adapter needed. */
  var projection = useMemo(function () {
    try {
      var result = holtWintersForecast(periodRows, { horizon: 8, granularityKey: priceSource });
      return Object.assign({ error: null }, result);
    } catch (err) {
      return { rows: [], mode: null, error: err && err.message ? err.message : String(err) };
    }
  }, [periodRows, priceSource]);

  var chartData = useMemo(function () {
    var clientsByKey = {}; internalWeekly.clients.forEach(function (r) { clientsByKey[r.year + "|" + r.weekIso] = r.avgPrice; });
    var suppliersByKey = {}; internalWeekly.suppliers.forEach(function (r) { suppliersByKey[r.year + "|" + r.weekIso] = r.avgPrice; });
    var actualPoints = periodRows.map(function (r) {
      var label = granularity === "daily" ? fmtDate(r.reportDate) : r.weekIso;
      var key = r.year + "|" + r.weekIso;
      return {
        label: label,
        usdaBand: (r.priceLow != null && r.priceHigh != null) ? [r.priceLow, r.priceHigh] : null,
        usdaMid: (r.priceLow != null && r.priceHigh != null) ? round2((r.priceLow + r.priceHigh) / 2) : null,
        clientsPrice: granularity === "weekly" ? (clientsByKey[key] != null ? round2(clientsByKey[key]) : null) : null,
        suppliersPrice: granularity === "weekly" ? (suppliersByKey[key] != null ? round2(suppliersByKey[key]) : null) : null,
        projectionMid: null, projectionBand: null,
      };
    });
    /* Projection only makes sense on the weekly axis — daily view's
       labels are calendar dates (fmtDate), not ISO week strings, so a
       weekly forecast row can't be placed on it without lying about the
       date. */
    var projectionPoints = (granularity === "weekly" && !projection.error) ? (projection.rows || []).map(function (r) {
      return { label: r.weekIso, usdaBand: null, usdaMid: null, clientsPrice: null, suppliersPrice: null, projectionMid: r.forecast, projectionBand: [r.low90, r.high90] };
    }) : [];
    return actualPoints.concat(projectionPoints);
  }, [periodRows, internalWeekly, granularity, projection]);

  var sortedTableRows = useMemo(function () {
    var q = search.trim().toLowerCase();
    var rows = periodRows.filter(function (r) {
      if (!q) return true;
      return [r.movement, r.trend, r.remarks, r.product, r.category, r.size].join(" ").toLowerCase().indexOf(q) >= 0;
    });
    rows = rows.slice().sort(function (a, b) {
      var va = a[sortCol], vb = b[sortCol];
      if (typeof va === "string") { var c = (va || "").localeCompare(vb || ""); return sortDir === "asc" ? c : -c; }
      va = parseFloat(va) || 0; vb = parseFloat(vb) || 0;
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return rows;
  }, [periodRows, search, sortCol, sortDir]);

  function yoyLow(row) {
    var targetYear = (row.year || 0) - 1;
    var match = periodRows.find(function (r) { return r.year === targetYear && r.weekIso === row.weekIso; })
      || filteredUsdaRows.find(function (r) { return r.year === targetYear && r.weekIso === row.weekIso; });
    if (!match || match.priceLow == null || row.priceLow == null) return null;
    return round2(row.priceLow - match.priceLow);
  }

  function toggleSort(col) {
    if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
    else { setSortCol(col); setSortDir("desc"); }
  }
  function arrow(col) { return sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : "⇅"; }

  var todayRows = useMemo(function () {
    var latest = filteredUsdaRows.reduce(function (acc, r) { return r.reportDate && r.reportDate > acc ? r.reportDate : acc; }, "");
    return filteredUsdaRows.filter(function (r) { return r.reportDate === latest; });
  }, [filteredUsdaRows]);

  /* Real trucks-equivalent (2026-09-15, replaces the old always-null mock
     field) — Mexico-origin truck crossings don't depend on price
     currency, so this counts regardless of the USDA/SNIIM priceSource
     toggle; filtered to the same product selection as the rest of the
     screen (no filter = all 3 berries). Trailing 7 days measured from
     truck_crossings' own latest shipment_date, not the price table's. */
  var trucks7d = useMemo(function () {
    var pSet = selProducts.length ? new Set(selProducts) : null;
    var rows = truckRows.filter(function (r) { return !pSet || pSet.has(r.product); });
    var latest = rows.reduce(function (acc, r) { return r.shipmentDate && r.shipmentDate > acc ? r.shipmentDate : acc; }, "");
    if (!latest) return 0;
    var from = addDaysISO(latest, -7);
    var lbTotal = rows.filter(function (r) { return r.shipmentDate > from && r.shipmentDate <= latest; })
      .reduce(function (s, r) { return s + (parseFloat(r.lbUnits) || 0); }, 0);
    return round2(lbTotal / LBS_PER_TRUCK);
  }, [truckRows, selProducts]);

  /* 2026-09-15 (Arturo): "trucks muestra 185.28 camiones pero no dice de
     que" — trucks7d silently summed across ALL 3 berries whenever no
     Product filter was picked, with no label saying so; he read it as
     one product's count. Label now spells out exactly which product(s)
     it covers, matching the same selProducts set the number itself
     filters by. */
  var trucksCardProductLabel = useMemo(function () {
    if (selProducts.length === 0) return t.trucksAllProducts;
    return selProducts.join(", ");
  }, [selProducts, t]);

  var latestSpread = useMemo(function () {
    var lastClient = internalWeekly.clients.slice(-1)[0];
    var lastSupplier = internalWeekly.suppliers.slice(-1)[0];
    var lastUsdaWeek = periodRows.slice(-1)[0];
    var usdaRow = lastUsdaWeek ? { priceLow: lastUsdaWeek.priceLow, priceHigh: lastUsdaWeek.priceHigh } : null;
    return {
      clients: usdaRow && lastClient ? computeSpreadVsUsda(lastClient, usdaRow) : null,
      suppliers: usdaRow && lastSupplier ? computeSpreadVsUsda(lastSupplier, usdaRow) : null,
    };
  }, [internalWeekly, periodRows]);

  function handleExport() {
    downloadXlsx("FobPrices", granularity === "daily" ? "Daily" : "Weekly", {
      Prices: sortedTableRows.map(function (r) {
        return {
          Date: r.reportDate, Week: r.weekIso, Product: r.product, Category: r.category, Size: r.size,
          Trucks: r.trucksEquivalent, "Price Low": r.priceLow, "Price High": r.priceHigh,
          Movement: r.movement, Trend: r.trend, "YoY Low": yoyLow(r), Remarks: r.remarks,
        };
      }),
    });
  }

  var noDataAtAll = usdaRows.length === 0;
  /* Compact header dropdowns (Fase 3B layout fix — see fix-dashboard.md):
     fixed 140px width so all 5 filters + title + sync fit one row on
     wide screens; SearchSelect's trigger is already 12px font, so no
     change needed there, just the wrapper width/gap here. */
  var compactSelectStyle = { width: 140 };

  return (
    <div>
      {toast && <Toast message={toast} onClose={function () { setToast(null); }} />}

      {/* Fila 1: título | filtros compactos | sync — wraps to a 2nd line
         on narrow screens since it's flex-wrap, no fixed breakpoint needed. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.source}</div>
            <SearchSelect value={priceSource} onChange={function (v) { setPriceSource(v); setSelCategories([]); }} style={{ ...compactSelectStyle, minWidth: 110 }} options={[{ value: "USDA", label: t.sourceUsda }, { value: "SNIIM", label: t.sourceSniim }]} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.product}</div>
            <SearchSelect multi value={selProducts} onChange={setSelProducts} placeholder={t.product} style={compactSelectStyle} options={PRODUCT_OPTIONS.map(function (p) { return { value: p, label: p }; })} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.category}</div>
            <SearchSelect multi value={selCategories} onChange={setSelCategories} placeholder={t.category} style={compactSelectStyle} options={CATEGORY_OPTIONS.map(function (c) { return { value: c, label: c }; })} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.size}</div>
            <SearchSelect multi value={selSizes} onChange={setSelSizes} placeholder={t.size} style={compactSelectStyle} options={SIZE_OPTIONS.map(function (s) { return { value: s, label: s }; })} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.dateRange}</div>
            <SearchSelect value={rangeKey} onChange={setRangeKey} style={compactSelectStyle} options={[
              { value: "today", label: t.rangeToday }, { value: "7", label: t.range7 }, { value: "30", label: t.range30 },
              { value: "60", label: t.range60 }, { value: "90", label: t.range90 }, { value: "custom", label: t.rangeCustom },
            ]} />
          </div>
          {rangeKey === "custom" && (
            <>
              <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.from}</div><input type="date" value={customFrom} onChange={function (e) { setCustomFrom(e.target.value); }} style={{ padding: "6px 8px", fontSize: 12, borderRadius: 5, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)" }} /></div>
              <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.to}</div><input type="date" value={customTo} onChange={function (e) { setCustomTo(e.target.value); }} style={{ padding: "6px 8px", fontSize: 12, borderRadius: 5, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)" }} /></div>
            </>
          )}
        </div>
      </div>

      {/* Fila 2: subtítulo + fuente de datos, compacto */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "2px 0 0" }}>{t.subtitle}</p>
        <p style={{ fontSize: 10, color: "var(--color-text-muted)", margin: "2px 0 0", fontStyle: "italic" }}>{dataSource === "real" ? t.dataSourceReal : dataSource === "mock" ? t.dataSourceMock : ""}</p>
        <span style={{ display: "inline-block", marginTop: 6, padding: "3px 9px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: priceSource === "SNIIM" ? "rgba(5,150,105,0.12)" : "rgba(37,99,235,0.12)", color: priceSource === "SNIIM" ? "#059669" : "#2563EB", border: "1px solid " + (priceSource === "SNIIM" ? "rgba(5,150,105,0.3)" : "rgba(37,99,235,0.3)") }}>{priceSource === "SNIIM" ? t.priceUnitSniim : t.priceUnitUsda}</span>
        {priceSource === "SNIIM" && <p style={{ fontSize: 10, color: "var(--color-text-muted)", margin: "6px 0 0", fontStyle: "italic" }}>{t.sniimNote}</p>}
      </div>

      {/* Sub-fase D.1 — USDA MARS border-price + SNIIM ingest status/
         manual trigger. This is the only ingest control on this screen
         now (the old Fase 3B "Sync now" button was removed 2026-09-12 —
         it hit a pipeline that never had real report slugs configured). */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 8, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, fontSize: 11, color: "var(--color-text-muted)" }}>
          <span>{t.lastIngestUsda}: {historicalStatus.usda && historicalStatus.usda.lastRunAt ? new Date(historicalStatus.usda.lastRunAt).toLocaleString() : t.never}{historicalStatus.usda && historicalStatus.usda.rowsIngested != null ? " → " + historicalStatus.usda.rowsIngested + " " + t.rows : ""}</span>
          <span>{t.lastIngestSniim}: {historicalStatus.sniim && historicalStatus.sniim.lastRunAt ? new Date(historicalStatus.sniim.lastRunAt).toLocaleString() : t.never}{historicalStatus.sniim && historicalStatus.sniim.rowsIngested != null ? " → " + historicalStatus.sniim.rowsIngested + " " + t.rows : ""}</span>
          <span>{t.totalHistoricalRecords}: <strong style={{ color: "var(--color-text)" }}>{historicalTotal}</strong></span>
        </div>
        <button onClick={handleRunIngestNow} disabled={runningIngest} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: runningIngest ? "var(--color-bg)" : "transparent", color: runningIngest ? "var(--color-text-muted)" : "var(--color-text-secondary)", fontSize: 11, fontWeight: 600, cursor: runningIngest ? "not-allowed" : "pointer" }}>{runningIngest ? t.runningIngest : t.runIngestNow}</button>
      </div>

      {noDataAtAll && (
        <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.25)", color: "#2563EB", fontSize: 12, fontWeight: 600, marginBottom: 14 }}>{t.emptyBanner}</div>
      )}

      {/* Today's cards */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        {todayRows.map(function (r, i) {
          return (
            <Card key={i}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{r.product} {r.category} {r.size}</div>
              <div style={{ display: "flex", gap: 14, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
                <div><div style={{ fontSize: 9, color: "var(--color-text-muted)" }}>{t.priceLow}</div><div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMoney(r.priceLow, currencyPrefix)}</div></div>
                <div><div style={{ fontSize: 9, color: "var(--color-text-muted)" }}>{t.priceHigh}</div><div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMoney(r.priceHigh, currencyPrefix)}</div></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11 }}>
                <span style={{ color: MOVEMENT_COLORS[r.movement] || "var(--color-text-muted)", fontWeight: 600 }}>{r.movement}</span>
                <span style={{ color: TREND_COLOR[r.trend] || "var(--color-text-muted)", fontWeight: 600 }}>{TREND_ARROW[r.trend] || ""} {r.trend}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.remarks}>{r.remarks}</div>
            </Card>
          );
        })}
        {priceSource === "USDA" && (
          <Card>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{t.spreadCard}</div>
            {[["vsClients", latestSpread.clients], ["vsSuppliers", latestSpread.suppliers]].map(function (pair) {
              var label = t[pair[0]]; var sp = pair[1];
              return (
                <div key={pair[0]} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 3 }}>
                  <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: sp ? (sp.favorable ? "#059669" : "#DC2626") : "var(--color-text-muted)" }}>
                    {sp ? (sp.favorable ? "▲ " : "▼ ") + fmtMoney(Math.abs(sp.spread)) : "—"}
                  </span>
                </div>
              );
            })}
          </Card>
        )}
        <Card>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{t.trucksCard}</div>
          <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>{trucks7d}</div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{trucksCardProductLabel}</div>
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{t.trucksCardNote}</div>
        </Card>
      </div>

      {/* Price Trend + Historical Prices — side by side, 2 equal columns.
         minmax(440px, 1fr) via auto-fit is this codebase's existing
         no-media-query responsive technique (see Dashboard.js's card
         grid): 2 columns fit while the container is wide enough for
         2×440px+gap (~900px), collapsing to 1 stacked column below
         that, same threshold fix-dashboard.md asked for without needing
         a hand-written @media breakpoint. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: 12 }}>

        <div style={{ border: "1px solid var(--color-border)", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{t.chartTitle}</h3>
            <div style={{ display: "flex", gap: 6 }}>
              {["daily", "weekly"].map(function (g) {
                var active = granularity === g;
                return <button key={g} onClick={function () { setGranularity(g); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + (active ? "var(--color-accent)" : "var(--color-border)"), background: active ? "var(--color-accent)" : "var(--color-bg-card)", color: active ? "var(--color-accent-text)" : "var(--color-text)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{g === "daily" ? t.granDaily : t.granWeekly}</button>;
              })}
            </div>
          </div>

          {priceSource === "USDA" && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 8 }}>
              {[["clients", t.toggleClients], ["suppliers", t.toggleSuppliers], ["both", t.toggleBoth]].map(function (pair) {
                var active = chartMode === pair[0];
                var disabled = granularity !== "weekly";
                return <button key={pair[0]} disabled={disabled} onClick={function () { setChartMode(pair[0]); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid " + (active ? "var(--color-accent)" : "var(--color-border)"), background: active && !disabled ? "var(--color-accent)" : "var(--color-bg-card)", color: disabled ? "var(--color-text-muted)" : (active ? "var(--color-accent-text)" : "var(--color-text)"), fontSize: 10, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}>{pair[1]}</button>;
              })}
            </div>
          )}
          {priceSource === "USDA" && granularity !== "weekly" && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{t.overlayWeeklyOnly}</div>}
          {granularity === "weekly" && projection.error && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{t.projectionUnavailable}</div>}
          {granularity === "weekly" && projection.mode === "trend-only" && <div style={{ fontSize: 10, color: "#B45309", marginBottom: 8, fontStyle: "italic" }}>{t.projectionTrendOnly}</div>}

          <div style={{ background: "var(--color-bg-card)", borderRadius: 10, padding: 12, height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={function (v) { return currencyPrefix + v; }} />
                <Tooltip formatter={function (v) { return Array.isArray(v) ? currencyPrefix + v[0] + " – " + currencyPrefix + v[1] : currencyPrefix + v; }} />
                <Legend />
                <Area type="monotone" dataKey="usdaBand" name={priceSource} stroke={CHART_COLORS.usda} fill={CHART_COLORS.usda} fillOpacity={0.15} connectNulls />
                <Line type="monotone" dataKey="usdaMid" name={priceSource + " (mid)"} stroke={CHART_COLORS.usdaMid} strokeWidth={2} dot={false} connectNulls />
                {priceSource === "USDA" && (chartMode === "clients" || chartMode === "both") && granularity === "weekly" && (
                  <Line type="monotone" dataKey="clientsPrice" name={t.seriesClients} stroke={CHART_COLORS.clients} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                )}
                {priceSource === "USDA" && (chartMode === "suppliers" || chartMode === "both") && granularity === "weekly" && (
                  <Line type="monotone" dataKey="suppliersPrice" name={t.seriesSuppliers} stroke={CHART_COLORS.suppliers} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                )}
                {granularity === "weekly" && !projection.error && (
                  <Area type="monotone" dataKey="projectionBand" name={t.seriesProjection + " (90%)"} stroke={CHART_COLORS.projection} fill={CHART_COLORS.projection} fillOpacity={0.12} />
                )}
                {granularity === "weekly" && !projection.error && (
                  <Line type="monotone" dataKey="projectionMid" name={t.seriesProjection} stroke={CHART_COLORS.projection} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ border: "1px solid var(--color-border)", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{t.tableTitle}</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)" }} />
              <button onClick={handleExport} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", color: "var(--color-text)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.exportExcel}</button>
            </div>
          </div>

          <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "auto", maxHeight: 420 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[["reportDate", t.colDate], ["weekIso", t.colWeek], ["trucksEquivalent", t.colTrucks], ["priceLow", t.priceLow], ["priceHigh", t.priceHigh], ["movement", t.movement], ["trend", t.trend], ["yoyLow", t.colYoyLow], ["remarks", t.remarks]].map(function (col) {
                    var isNum = !!RIGHT_NUM_COLS[col[0]];
                    return (
                      <th key={col[0]} onClick={function () { toggleSort(col[0]); }} style={{ padding: "8px 8px", paddingRight: isNum ? 16 : 8, textAlign: isNum ? "right" : "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)", cursor: "pointer", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, flexDirection: isNum ? "row-reverse" : "row" }}>
                          <span>{col[1]}</span><span style={{ fontSize: 9 }}>{arrow(col[0])}</span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedTableRows.map(function (r, i) {
                  var yl = yoyLow(r);
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "7px 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{fmtDate(r.reportDate)}</td>
                      <td style={{ padding: "7px 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{r.weekIso}</td>
                      <td style={{ padding: "7px 8px", paddingRight: 16, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{r.trucksEquivalent}</td>
                      <td style={{ padding: "7px 8px", paddingRight: 16, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmtMoney(r.priceLow, currencyPrefix)}</td>
                      <td style={{ padding: "7px 8px", paddingRight: 16, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmtMoney(r.priceHigh, currencyPrefix)}</td>
                      <td style={{ padding: "7px 8px", fontSize: 11, fontWeight: 600, color: MOVEMENT_COLORS[r.movement] || "var(--color-text)" }}>{r.movement}</td>
                      <td style={{ padding: "7px 8px", fontSize: 11, fontWeight: 600, color: TREND_COLOR[r.trend] || "var(--color-text)" }}>{TREND_ARROW[r.trend] || ""} {r.trend}</td>
                      <td style={{ padding: "7px 8px", paddingRight: 16, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", textAlign: "right", color: yl == null ? "var(--color-text-muted)" : (yl >= 0 ? "#059669" : "#DC2626") }}>{yl == null ? "—" : (yl >= 0 ? "▲ " : "▼ ") + fmtMoney(Math.abs(yl))}</td>
                      <td style={{ padding: "7px 8px", fontSize: 11, color: "var(--color-text-muted)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.remarks}>{r.remarks}</td>
                    </tr>
                  );
                })}
                {sortedTableRows.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--color-text-muted)" }}>{t.noRows}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
